import {Alert, Button, Checkbox, Divider, Input, InputNumber, message, Modal, Radio, Table, Typography} from "antd";
import {QueryOptions} from "@apollo/client";
import moment from "moment";
import React, {FC, memo, useEffect, useMemo, useRef, useState} from "react";
import uuid from "uuid";
import {BookingPostKind, CancelRestHandling,
    GetBookingsByCourseDocument,
    GetCourseByIdDocument,
    PaymentProvider,
    ScheduledPaymentType,
    useCancelBookingAndGenerateReversalInvoiceMutation,
    useGetAppSettingsQuery,
    useGetBookingByIdQuery,
    useRefundMolliePaymentFromAdminMutation,} from "../../../generated/graphql";
import i18n from "../../../services/i18n";
import {TableType} from "./BookingListColumns";
import css from "./ReverseBookingModal.less";
import {useCurrency} from "../../../helpers/currencyHelper";
import {useCurrentUserRoles} from "../../../helpers/useIsStaffOnlyAccess";
import Roles, {hasRoleAtLeast} from "../../../../shared/models/roles";
import {isCoursePost, isOpenFee, paidAmountOf, planCancelSettlement} from "../../../../shared/cancelSettlementPlan";
import {getRefundCap} from "../../../views/RefundPanel/RefundPanel";
import {renderAmount} from "../../../helpers/renderAmount";

type ReverseBookingModalProps = {
    open: boolean;
    closeModal: () => void;
    record: TableType;
    confirmAction?: (e: boolean) => void;
    refetch?: () => void;
};

const tr = () => i18n.containers.bookings.BookingList.bookingReversal;

/**
 * Storno einer abgerechneten Buchung mit Entscheidung je Posten (ADR 0022). Offene Kursraten gelten als erlassen;
 * je offener Gebühr „bleibt offen / erlassen“, Stornogebühr mit Tenant-Vorschlag, Verrechnung aus bezahltem Geld,
 * vier Wege für den Rest. Die Vorschau rechnet mit demselben Planer wie der Server (`shared/cancelSettlementPlan`).
 */
const ReverseBookingModal: React.FC<ReverseBookingModalProps> = ({open = false, closeModal, record, confirmAction = (e) => console.log(e), refetch}) => {
    const t = tr();
    const currency = useCurrency();
    const {roles} = useCurrentUserRoles();
    const canRefundMollie = hasRoleAtLeast(roles, Roles.Admin);
    const {data: appSettingsData} = useGetAppSettingsQuery();
    const [sendReversalInvoice, setSendReversalInvoice] = useState(false);
    const [feeDecisions, setFeeDecisions] = useState<Record<string, boolean>>({});
    const [cancelationFee, setCancelationFee] = useState<number | undefined>(undefined);
    const [settle, setSettle] = useState(true);
    const [restHandling, setRestHandling] = useState<CancelRestHandling>(CancelRestHandling.Credit);
    const [restNote, setRestNote] = useState("");
    // Sperre über Refunds UND Storno: der Mutation-Hook kennt nur seinen eigenen Aufruf (Codex #1710)
    const [submitting, setSubmitting] = useState(false);
    // Schon ausgelöste Mollie-Erstattungen dieses Dialogs: scheitert der Storno danach, zeigt die neu geladene Buchung
    // keinen Rest mehr; der zweite Versuch muss Weg und Betrag trotzdem melden (claude-review #1710).
    const mollieRefundedRef = useRef(0);

    const invoiceId = record.invoice?.id;
    const email = record.attendee?.booker?.email;
    const {data: bookingByIdData, refetch: refetchBooking} = useGetBookingByIdQuery({variables: {id: record.id}, skip: !open || !record.id, fetchPolicy: "cache-and-network"});
    const booking = bookingByIdData?.bookingById;
    const posts = useMemo(() => booking?.scheduledPayments ?? [], [booking]);
    const existingCancelationFee = posts.some((sp) => sp.type === ScheduledPaymentType.CancelationFee && !sp.paymentDone);

    useEffect(() => {
        if (!open) return;
        setSendReversalInvoice(appSettingsData?.settings?.sendReversalInvoice || false);
        setCancelationFee(existingCancelationFee ? undefined : Number(appSettingsData?.settings?.cancelationFeeDefault ?? 0) || undefined);
        setFeeDecisions({});
        setSettle(true);
        setRestHandling(CancelRestHandling.Credit);
        setRestNote("");
    }, [open, appSettingsData, existingCancelationFee]);

    const openFees = useMemo(() => posts.filter(isOpenFee), [posts]);
    const paidPosts = useMemo(() => posts.filter((sp) => isCoursePost(sp) && paidAmountOf(sp) > 0), [posts]);
    const waivedCoursePosts = useMemo(() => posts.filter((sp) => isCoursePost(sp) && !sp.paymentDone && Number(sp.openAmount ?? sp.paymentAmount) > 0), [posts]);
    const plan = useMemo(
        () =>
            planCancelSettlement({
                posts,
                decisions: Object.keys(feeDecisions).map((id) => ({scheduledPaymentId: id, keep: feeDecisions[id]})),
                cancelationFeeAmount: existingCancelationFee ? 0 : cancelationFee,
                settleFeesFromPayments: settle,
            }),
        [posts, feeDecisions, cancelationFee, settle, existingCancelationFee],
    );
    // Mollie nur, wenn die erstattbaren Mollie-Zahlungen den ganzen Rest decken; keine Aufteilung auf zwei Wege.
    const mollieRows = useMemo(
        () =>
            posts
                .filter((sp) => isCoursePost(sp) && sp.paymentProvider === PaymentProvider.Mollie && sp.paymentDone)
                .map((sp) => ({
                    scheduledPaymentId: sp.id,
                    paymentAmount: Number(sp.paymentAmount),
                    refundedAmount: Number(sp.refundedAmount || 0),
                    paymentDue: sp.paymentDue,
                    paymentDone: sp.paymentDone ?? null,
                    title: sp.title ?? null,
                    paymentProvider: sp.paymentProvider,
                    molliePaidAmount: sp.molliePaidAmount,
                }))
                .filter((row) => getRefundCap(row) > 0)
                .sort((a, b) => moment(b.paymentDue).valueOf() - moment(a.paymentDue).valueOf()),
        [posts],
    );
    const mollieCap = mollieRows.reduce((sum, row) => sum + getRefundCap(row), 0);
    const mollieAllowed = canRefundMollie && mollieRows.length > 0 && mollieCap + 0.005 >= plan.rest;

    useEffect(() => {
        if (restHandling === CancelRestHandling.MollieRefund && !mollieAllowed) setRestHandling(CancelRestHandling.Credit);
    }, [mollieAllowed, restHandling]);

    // Gleiche Schreibweise wie Gebührenfeld und Mail (Komma bei EUR, Schweizer Format bei CHF)
    const money = (amount: number) => renderAmount({amount, currency: currency.short as "EUR" | "CHF" | "USD" | "GBP"});
    const [refundMolliePayment] = useRefundMolliePaymentFromAdminMutation();

    const refetchQueries: Array<string | QueryOptions> = [
        "GetSettledBookings",
        "GetUnsettledBookings",
        "GetBookingsOverviewOptimized",
    ];

    if (record.courseId) {
        refetchQueries.push({query: GetBookingsByCourseDocument, variables: {id: record.courseId}});
        refetchQueries.push({query: GetCourseByIdDocument, variables: {id: record.courseId}});
    }

    const [cancelBookingAndCreateReversal, {loading}] = useCancelBookingAndGenerateReversalInvoiceMutation({awaitRefetchQueries: true, refetchQueries});

    const handleOk = async () => {
        if (!invoiceId || submitting) return;
        setSubmitting(true);
        const sendMail = Boolean(email) && sendReversalInvoice;
        // Eine Korrelations-ID für die ganze Storno-Aktion (Refunds + Storno), damit die Historie EINEN Block zeigt.
        const correlationContext = {headers: {"x-correlation-id": uuid()}};

        try {
            // Mollie-Erstattung VOR dem Storno: den Rest auf die Mollie-Raten verteilen, späteste zuerst.
            let mollieRefundedAmount = mollieRefundedRef.current;

            if (plan.rest > 0.005 && restHandling === CancelRestHandling.MollieRefund) {
                let remaining = plan.rest;

                for (const row of mollieRows) {
                    if (remaining <= 0.005) break;
                    const amount = Math.round(Math.min(getRefundCap(row), remaining) * 100) / 100;

                    await refundMolliePayment({variables: {scheduledPaymentId: row.scheduledPaymentId, amount, reason: restNote || undefined}, context: correlationContext});
                    remaining = Math.round((remaining - amount) * 100) / 100;
                    mollieRefundedAmount = Math.round((mollieRefundedAmount + amount) * 100) / 100;
                    mollieRefundedRef.current = mollieRefundedAmount;
                }
            }

            await cancelBookingAndCreateReversal({
                variables: {
                    id: invoiceId,
                    sendMail,
                    options: {
                        cancelationFeeAmount: existingCancelationFee ? 0 : cancelationFee ?? 0,
                        feeDecisions: openFees.map((fee) => ({scheduledPaymentId: fee.id, keep: feeDecisions[fee.id] !== false})),
                        settleFeesFromPayments: settle,
                        restHandling: mollieRefundedAmount > 0 ? CancelRestHandling.MollieRefund : plan.rest > 0.005 ? restHandling : undefined,
                        restNote: restNote || undefined,
                        ...(mollieRefundedAmount > 0 ? {mollieRefundedAmount} : {}),
                    },
                },
                context: correlationContext,
            });
            confirmAction(true);
            refetch?.();
            closeModal();
            message.success(t.messageSuccess(), 3);
        } catch (error) {
            // Nach einem Fehler die Buchung neu laden: liefen Refunds schon, sinkt das Bezahlte um den erstatteten
            // Betrag, und ein zweiter Versuch erstattet nicht noch einmal.
            await refetchBooking();
            message.error(`Refund/Storno fehlgeschlagen: ${error instanceof Error ? error.message : String(error)}`, 5);
        } finally {
            setSubmitting(false);
        }
    };

    const handleCancel = () => {
        refetch?.();
        confirmAction(false);
        closeModal();
    };

    const invoiceNumber = booking?.invoice ? `${booking.invoice.invoiceNumberPrefix ?? ""}-${booking.invoice.invoiceNumber ?? ""}`.replace(/^-/, "") : "";
    const waivedAmount = waivedCoursePosts.reduce((sum, sp) => sum + Number(sp.openAmount ?? sp.paymentAmount), 0);
    // Kursgebühr nach postKind (Server-Einordnung), nicht nach Typ: umbenannte Kursraten sind Custom, aber keine Zusatzposten (Review #1790)
    const isCourseFee = (sp: {postKind?: BookingPostKind | null; type: ScheduledPaymentType}) => (sp.postKind ? sp.postKind === BookingPostKind.CourseFee : sp.type === ScheduledPaymentType.Standard);
    const waivedRates = waivedCoursePosts.filter(isCourseFee).length;
    const waivedExtras = waivedCoursePosts.length - waivedRates;
    // Satz nach Inhalt: „3 offene Kursgebühren (60,00 €) gelten damit als erlassen.“, Zusatzposten nur, wenn es welche gibt
    const waivedSentence =
        waivedCoursePosts.length === 0
            ? t.waivedNone()
            : `${[waivedRates > 0 ? t.waivedRates({count: String(waivedRates)}) : "", waivedExtras > 0 ? t.waivedExtras({count: String(waivedExtras)}) : ""].filter(Boolean).join(t.waivedJoin())} (${money(waivedAmount)}) ${t.waivedSuffix({count: String(waivedCoursePosts.length)})}`;

    return (
        <Modal
            title={t.modalTitle()}
            open={open}
            onCancel={handleCancel}
            destroyOnClose
            width={640}
            footer={[
                <Button key="cancelReversalInvoice" onClick={handleCancel}>
                    {t.cancel()}
                </Button>,
                <Button key="okReversalInvoice" type="primary" danger onClick={handleOk} loading={loading || submitting} data-testid="reverse-booking-confirm">
                    {!email ? t.cancelNow() : sendReversalInvoice ? t.cancelWithNotification() : t.cancelWithoutNotification()}
                </Button>,
            ]}
        >
            <Alert
                type="warning"
                showIcon
                message={t.messageHeader()}
                description={
                    <>
                        {t.messageP1({invoiceNumber})} {waivedSentence} {t.messageP2()}
                    </>
                }
            />

            {openFees.length > 0 && (
                <div className={css.section} data-testid="reverse-booking-fees">
                    <Typography.Text strong>{t.fees.title()}</Typography.Text>
                    <Table
                        size="small"
                        pagination={false}
                        rowKey="id"
                        dataSource={openFees}
                        columns={[
                            {title: t.fees.columnFee(), dataIndex: "title", render: (title: string | null, sp) => title || sp.type},
                            {title: t.fees.columnOpen(), align: "right", render: (_, sp) => money(Number(sp.openAmount ?? sp.paymentAmount))},
                            {
                                title: t.fees.columnDecision(),
                                render: (_, sp) => (
                                    <Radio.Group value={feeDecisions[sp.id] !== false ? "keep" : "waive"} onChange={(e) => setFeeDecisions({...feeDecisions, [sp.id]: e.target.value === "keep"})}>
                                        <Radio value="keep">{t.fees.keep()}</Radio>
                                        <Radio value="waive">{t.fees.waive()}</Radio>
                                    </Radio.Group>
                                ),
                            },
                        ]}
                    />
                    <Typography.Text type="secondary" className={css.hint}>
                        {t.fees.hint()}
                    </Typography.Text>
                </div>
            )}

            <div className={css.section} data-testid="reverse-booking-cancelation-fee">
                <Typography.Text strong>{t.cancelationFee.title()}</Typography.Text>
                <div className={css.row}>
                    <InputNumber
                        min={0}
                        max={100000}
                        precision={2}
                        decimalSeparator=","
                        value={cancelationFee}
                        onChange={(value) => setCancelationFee(typeof value === "number" ? value : undefined)}
                        disabled={existingCancelationFee}
                        addonAfter={currency.symbol}
                    />
                    <Typography.Text type="secondary" className={css.hint}>
                        {existingCancelationFee
                            ? t.cancelationFee.existing()
                            : Number(appSettingsData?.settings?.cancelationFeeDefault ?? 0) > 0
                            ? t.cancelationFee.hint()
                            : t.cancelationFee.hintNone()}
                    </Typography.Text>
                </div>
            </div>

            {paidPosts.length > 0 && (
                <div className={css.section} data-testid="reverse-booking-paid">
                    <Typography.Text strong>{t.paid.title({amount: money(plan.available)})}</Typography.Text>
                    <Table
                        size="small"
                        pagination={false}
                        rowKey="id"
                        dataSource={paidPosts}
                        columns={[
                            {title: t.paid.columnPost(), dataIndex: "title", render: (title: string | null, sp) => title || (isCourseFee(sp) ? t.paid.courseFee() : t.paid.additionalItem())},
                            {title: t.paid.columnAmount(), align: "right", render: (_, sp) => money(paidAmountOf(sp))},
                            {
                                title: t.paid.columnPaid(),
                                render: (_, sp) => `${sp.paymentDone ? moment(sp.paymentDone).format("DD.MM.YYYY") : ""}${sp.paymentProvider === PaymentProvider.Mollie ? " · Mollie" : ""}`,
                            },
                        ]}
                    />
                    {plan.feesOpen > 0 && (
                        <Checkbox checked={settle} onChange={(e) => setSettle(e.target.checked)} className={css.settle}>
                            {t.paid.settle({amount: money(Math.min(plan.available, plan.feesOpen))})}
                        </Checkbox>
                    )}
                    <div className={css.sum}>
                        <span>{t.paid.rest()}</span>
                        <span>{money(plan.rest)}</span>
                    </div>
                    {plan.rest > 0.005 && (
                        <>
                            <Typography.Text strong className={css.question}>
                                {t.paid.question({amount: money(plan.rest)})}
                            </Typography.Text>
                            <Radio.Group value={restHandling} onChange={(e) => setRestHandling(e.target.value)} className={css.restOptions} data-testid="reverse-booking-rest">
                                <Radio value={CancelRestHandling.MollieRefund} disabled={!mollieAllowed}>
                                    {t.paid.mollie()}
                                </Radio>
                                <Radio value={CancelRestHandling.Credit}>{t.paid.credit()}</Radio>
                                <Radio value={CancelRestHandling.ManualRefund}>{t.paid.manualRefund()}</Radio>
                                <Radio value={CancelRestHandling.KeptBySchool}>{t.paid.kept()}</Radio>
                            </Radio.Group>
                            <Typography.Text type="secondary" className={css.hint}>
                                {restHandling === CancelRestHandling.Credit
                                    ? t.paid.creditHint()
                                    : restHandling === CancelRestHandling.KeptBySchool
                                    ? t.paid.keptHint()
                                    : !mollieAllowed && mollieRows.length > 0
                                    ? t.paid.mollieDisabled()
                                    : ""}
                            </Typography.Text>
                            {(restHandling === CancelRestHandling.ManualRefund || restHandling === CancelRestHandling.KeptBySchool || restHandling === CancelRestHandling.MollieRefund) && (
                                <Input value={restNote} onChange={(e) => setRestNote(e.target.value)} placeholder={t.paid.notePlaceholder()} maxLength={200} className={css.note} />
                            )}
                        </>
                    )}
                </div>
            )}
            {plan.openAfter > 0.005 && (
                <div className={css.sum} data-testid="reverse-booking-open-after">
                    <span>{t.paid.openAfter()}</span>
                    <span>{money(plan.openAfter)}</span>
                </div>
            )}

            <Divider />
            {email && (
                <Checkbox checked={sendReversalInvoice} onChange={(e) => setSendReversalInvoice(e.target.checked)}>
                    {t.sendReceipt({email})}
                </Checkbox>
            )}
        </Modal>
    );
};

export default memo(ReverseBookingModal);
